import * as React from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { RouteComponentProps, withRouter } from 'react-router'
import { fleetObserverPath, tasksPath } from '@logic'
import { TasksSidebarLink } from '@shared/components/sidebar'
import { useTaskThreadSubscription } from '@src/modules/task-threads/hooks/useTaskThreadSubscription'
import type { RootState } from '@logic/types/types'
import {
  buildFleetNativePanelRoute,
  buildFleetCommandCenterRoute,
  FLEET_NAVIGATION_GROUPS,
  FleetNavigationItem,
  getFleetPanelFromSearch,
} from '../services/fleet-navigation'

const Layout = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  min-width: 0;

  @media (max-width: 920px) {
    flex-direction: column;
  }
`

const Main = styled.div`
  flex: 1;
  min-width: 0;
`

const Sidebar = styled.nav`
  width: 264px;
  min-width: 264px;
  align-self: flex-start;
  border: 1px solid rgba(156, 166, 173, 0.32);
  background: #1f313a;
  color: #9ca6ad;
  box-sizing: border-box;

  @media (max-width: 920px) {
    width: 100%;
    min-width: 0;
  }
`

const Header = styled.div`
  min-height: 58px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(156, 166, 173, 0.22);
  box-sizing: border-box;
`

const Mark = styled.div`
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: #33aa40;
  color: #fff;
  font-family: 'NunitoSansBold';
  font-size: 14px;
`

const TitleWrap = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const Title = styled.span`
  color: #fff;
  font-family: 'NunitoSansBold';
  font-size: 15px;
  line-height: 20px;
`

const Caption = styled.span`
  color: #9ca6ad;
  font-size: 11px;
  line-height: 15px;
`

const NavBody = styled.div`
  max-height: calc(100vh - 190px);
  overflow-y: auto;
  padding: 12px 8px 14px;

  @media (max-width: 920px) {
    max-height: none;
  }
`

const TasksLinkWrap = styled.div`
  padding: 10px 8px 0;
`

const Group = styled.div`
  & + & {
    margin-top: 14px;
    padding-top: 10px;
    border-top: 1px solid rgba(156, 166, 173, 0.18);
  }
`

const GroupTitle = styled.div`
  padding: 0 10px 7px;
  color: rgba(156, 166, 173, 0.92);
  font-family: 'NunitoSansSemiBold';
  font-size: 11px;
  line-height: 15px;
  text-transform: uppercase;
`

const NavItem = styled(Link as any)<{ $selected: boolean }>`
  min-height: 50px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 10px;
  color: ${(p) => (p.$selected ? '#fff' : '#9ca6ad')};
  background: ${(p) => (p.$selected ? 'rgba(71, 97, 113, 0.72)' : 'transparent')};
  border-radius: 4px;
  text-decoration: none;
  box-sizing: border-box;
  position: relative;

  &:hover {
    color: #fff;
    background: rgba(71, 97, 113, 0.42);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #33aa40;
  }

  &::before {
    content: ${(p) => (p.$selected ? "''" : 'none')};
    position: absolute;
    left: 0;
    top: 10px;
    width: 3px;
    height: 30px;
    border-radius: 2px;
    background: #33aa40;
  }
`

const Icon = styled.span<{ $selected: boolean }>`
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 5px;
  border: 1px solid ${(p) => (p.$selected ? '#33aa40' : 'rgba(156, 166, 173, 0.28)')};
  color: ${(p) => (p.$selected ? '#33aa40' : 'currentColor')};
  font-family: 'NunitoSansSemiBold';
  font-size: 11px;
  line-height: 1;
`

const Text = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ItemTitle = styled.span`
  color: currentColor;
  font-family: 'NunitoSansSemiBold';
  font-size: 13px;
  line-height: 17px;
`

const ItemRoute = styled.code`
  color: rgba(206, 218, 225, 0.72);
  font-family: monospace;
  font-size: 10px;
  line-height: 14px;
  word-break: break-word;
`

const Badge = styled.span`
  flex: 0 0 auto;
  min-width: 34px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(51, 170, 64, 0.9);
  color: #fff;
  font-size: 10px;
`

function itemRoute(item: FleetNavigationItem, pathname: string, search: string): string {
  if (item.panel) return buildFleetNativePanelRoute(item.panel, search, pathname)
  if (item.commandCenter) return buildFleetCommandCenterRoute(search, pathname, item.commandCenterTab)
  return item.route
}

interface RouteParts {
  pathname: string
  search: string
  hash: string
}

function parseRoute(route: string): RouteParts {
  const hashIndex = route.indexOf('#')
  const beforeHash = hashIndex >= 0 ? route.slice(0, hashIndex) : route
  const hash = hashIndex >= 0 ? route.slice(hashIndex) : ''
  const searchIndex = beforeHash.indexOf('?')
  const pathname = searchIndex >= 0 ? beforeHash.slice(0, searchIndex) : beforeHash
  const search = searchIndex >= 0 ? beforeHash.slice(searchIndex) : ''

  return { pathname, search, hash }
}

function matchesRoute(route: string, pathname: string, search: string, hash: string): boolean {
  const target = parseRoute(route)
  if (pathname !== target.pathname) return false
  if (target.search && search !== target.search) return false
  if (target.hash && hash !== target.hash) return false
  if (!target.hash && hash) return false
  return true
}

function isSelected(item: FleetNavigationItem, pathname: string, search: string, hash: string): boolean {
  if (item.panel) {
    if (pathname === fleetObserverPath) return getFleetPanelFromSearch(search) === item.panel
    return matchesRoute(buildFleetNativePanelRoute(item.panel, search, pathname), pathname, search, hash)
  }
  return matchesRoute(item.route, pathname, search, hash)
}

function itemInitial(title: string): string {
  return title.slice(0, 1).toUpperCase()
}

function selectTaskThreadCompanyId(state: RootState): string | null {
  return state.organization?.currentOrganization?.id ?? null
}

const FleetNavigationSidebarInner: React.FC<RouteComponentProps> = ({ location, history }) => {
  const companyId = useSelector(selectTaskThreadCompanyId)
  const taskSelected = location.pathname === tasksPath || location.pathname.startsWith(`${tasksPath}/`)

  useTaskThreadSubscription({
    companyId,
    surfaceIdentity: 'equa-web-fleet-sidebar',
    enabled: Boolean(companyId),
  })

  return (
    <Sidebar aria-label="Fleet navigation" data-testid="fleet-navigation-sidebar">
      <Header>
        <Mark>F</Mark>
        <TitleWrap>
          <Title>Command Center</Title>
          <Caption>Fleet operations</Caption>
        </TitleWrap>
      </Header>
      <TasksLinkWrap>
        <TasksSidebarLink
          active={taskSelected}
          onClick={() => history.push(tasksPath)}
          style={{
            color: taskSelected ? '#fff' : '#d7dee2',
            background: taskSelected ? 'rgba(51, 170, 64, 0.18)' : 'rgba(255, 255, 255, 0.06)',
          }}
        />
      </TasksLinkWrap>
      <NavBody>
        {FLEET_NAVIGATION_GROUPS.map((group) => (
          <Group key={group.title}>
            <GroupTitle>{group.title}</GroupTitle>
            {group.items.map((item) => {
              const selected = isSelected(item, location.pathname, location.search, location.hash)
              const route = itemRoute(item, location.pathname, location.search)
              return (
                <NavItem
                  key={`${group.title}:${item.title}`}
                  to={route}
                  $selected={selected}
                  aria-current={selected ? 'page' : undefined}
                  title={item.description}
                >
                  <Icon $selected={selected}>{itemInitial(item.title)}</Icon>
                  <Text>
                    <ItemTitle>{item.title}</ItemTitle>
                    <ItemRoute>{route}</ItemRoute>
                  </Text>
                  {item.badge ? <Badge>{item.badge}</Badge> : null}
                </NavItem>
              )
            })}
          </Group>
        ))}
      </NavBody>
    </Sidebar>
  )
}

export const FleetNavigationSidebar = withRouter(FleetNavigationSidebarInner as any) as React.ComponentType<{}>

export const FleetNavigationLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Layout>
    <FleetNavigationSidebar />
    <Main>{children}</Main>
  </Layout>
)
